import { makeStyles, Typography, Paper, Box, Link, Container, AppBar, Toolbar } from '@material-ui/core';

// Badal by TELUS brand colors
const badalPrimaryBlue = '#1e87f0';
const badalSecondaryGreen = '#32d296';
const badalTeal = '#5bbad5';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
  },
  appBar: {
    background: `linear-gradient(135deg, ${badalPrimaryBlue} 0%, ${badalTeal} 100%)`,
    color: '#ffffff',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  logo: {
    height: 36,
    filter: 'brightness(0) invert(1)',
  },
  headerTitle: {
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
  byTelus: {
    fontSize: '0.75rem',
    opacity: 0.9,
    marginLeft: theme.spacing(1),
    fontWeight: 400,
  },
  content: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 900,
    margin: '0 auto',
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    lineHeight: 1.7,
  },
  list: {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    '& li': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.6,
    },
  },
  lastUpdated: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  footer: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
  },
  footerBranding: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
    textAlign: 'center',
  },
  brandAccent: {
    color: badalPrimaryBlue,
    fontWeight: 600,
  },
  linkAccent: {
    color: badalPrimaryBlue,
    '&:hover': {
      color: badalSecondaryGreen,
    },
  },
}));

export const PrivacyPolicy = () => {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <AppBar position="static" className={classes.appBar} elevation={2}>
        <Toolbar className={classes.toolbar}>
          <img src="/badal-logo.png" alt="Badal" className={classes.logo} />
          <Typography variant="h6" className={classes.headerTitle}>
            Internal Developer Portal
            <span className={classes.byTelus}>by TELUS</span>
          </Typography>
        </Toolbar>
      </AppBar>
      <Container className={classes.content}>
        <Paper className={classes.paper}>
          <Typography variant="h4" className={classes.title}>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            Badal Internal Developer Portal
          </Typography>
          <Typography variant="body2" className={classes.lastUpdated}>
            Last Updated: January 2025
          </Typography>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              1. Introduction
            </Typography>
            <Typography className={classes.paragraph}>
              This Privacy Policy describes how Badal.io ("we", "us", or "our") collects, uses, and
              protects information when you use the Badal Internal Developer Portal ("the Portal").
              This Portal is an internal tool designed exclusively for Badal and TELUS employees
              to support software development activities.
            </Typography>
            <Typography className={classes.paragraph}>
              By accessing and using this Portal, you acknowledge that you have read and understood
              this Privacy Policy and agree to the collection and use of information in accordance
              with this policy.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              2. Information We Collect
            </Typography>
            <Typography className={classes.paragraph}>
              The Portal collects and processes the following types of information:
            </Typography>
            <Typography variant="h6" gutterBottom>Authentication Information</Typography>
            <ul className={classes.list}>
              <li>Corporate identity credentials (via SSO/OAuth)</li>
              <li>GitHub username and organization memberships</li>
              <li>Google Cloud Platform identity for GCP resource access</li>
            </ul>
            <Typography variant="h6" gutterBottom>Usage Information</Typography>
            <ul className={classes.list}>
              <li>Pages visited within the Portal</li>
              <li>Features and tools accessed</li>
              <li>Search queries within the Portal</li>
              <li>User preferences and settings</li>
            </ul>
            <Typography variant="h6" gutterBottom>Development Activity Data</Typography>
            <ul className={classes.list}>
              <li>GitHub repository interactions (commits, pull requests, issues)</li>
              <li>GCP project access and resource usage</li>
              <li>Software catalog interactions</li>
              <li>Documentation access patterns</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              3. How We Use Your Information
            </Typography>
            <Typography className={classes.paragraph}>
              All information collected is used exclusively for internal development purposes:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Authentication and Authorization:</strong> To verify your identity and provide appropriate access to resources</li>
              <li><strong>Portal Functionality:</strong> To provide personalized dashboards, show relevant repositories, and display your development activity</li>
              <li><strong>Improving the Portal:</strong> To understand usage patterns and improve the Portal experience</li>
              <li><strong>Security:</strong> To detect and prevent unauthorized access or security incidents</li>
              <li><strong>Support:</strong> To assist with troubleshooting and user support requests</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              4. Data Storage and Security
            </Typography>
            <Typography className={classes.paragraph}>
              We implement appropriate technical and organizational measures to protect your information:
            </Typography>
            <ul className={classes.list}>
              <li>Data is stored in secure Google Cloud Platform infrastructure</li>
              <li>All data transmission is encrypted using TLS/HTTPS</li>
              <li>Access to data is restricted to authorized personnel only</li>
              <li>Regular security assessments are conducted</li>
              <li>Authentication tokens are stored securely and expire appropriately</li>
            </ul>
            <Typography className={classes.paragraph}>
              User preferences and Portal settings are stored in a secure database. GitHub and GCP
              access tokens are handled in accordance with OAuth best practices and are not stored
              permanently.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              5. Data Sharing
            </Typography>
            <Typography className={classes.paragraph}>
              Your information is <strong>not shared with third parties</strong> outside of Badal and TELUS.
              Information may be shared:
            </Typography>
            <ul className={classes.list}>
              <li>Within Badal and TELUS for legitimate business purposes related to development operations</li>
              <li>With GitHub and Google Cloud Platform as necessary to provide Portal functionality (using standard API integrations)</li>
              <li>When required by law or to protect our rights and safety</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              6. Data Retention
            </Typography>
            <Typography className={classes.paragraph}>
              We retain your information only as long as necessary for the purposes outlined in this policy:
            </Typography>
            <ul className={classes.list}>
              <li>User preferences are retained while your account is active</li>
              <li>Usage logs are retained for a maximum of 90 days</li>
              <li>Authentication session data expires according to corporate security policies</li>
            </ul>
            <Typography className={classes.paragraph}>
              When your employment ends, your Portal data will be deleted in accordance with
              corporate offboarding procedures.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              7. Your Rights
            </Typography>
            <Typography className={classes.paragraph}>
              As a user of this internal Portal, you have the right to:
            </Typography>
            <ul className={classes.list}>
              <li>Access information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your preferences and settings</li>
              <li>Understand how your information is being used</li>
            </ul>
            <Typography className={classes.paragraph}>
              To exercise these rights, please contact your manager or the Portal administrators.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              8. Cookies and Local Storage
            </Typography>
            <Typography className={classes.paragraph}>
              The Portal uses browser storage technologies to improve your experience:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Session cookies:</strong> Required for authentication and security</li>
              <li><strong>Local storage:</strong> Used to store user preferences and cached data for performance</li>
              <li><strong>Theme preferences:</strong> Your selected visual theme is stored locally</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              9. Changes to This Policy
            </Typography>
            <Typography className={classes.paragraph}>
              We may update this Privacy Policy from time to time. Changes will be posted on this page
              with an updated revision date. Significant changes may be communicated through the Portal
              or corporate communication channels.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              10. Contact
            </Typography>
            <Typography className={classes.paragraph}>
              If you have questions about this Privacy Policy or how your information is handled,
              please contact the Badal Platform Team or your direct manager.
            </Typography>
          </Box>

          <Box className={classes.footer}>
            <Typography variant="body2" color="textSecondary">
              This Privacy Policy applies to the Badal Internal Developer Portal. For questions about
              TELUS corporate privacy policies, please refer to the{' '}
              <Link href="https://www.telus.com/en/about/privacy" target="_blank" rel="noopener" className={classes.linkAccent}>
                TELUS Privacy Commitment
              </Link>.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              <Link href="/terms-of-service" className={classes.linkAccent}>Terms of Service</Link>
            </Typography>
          </Box>
          <Box className={classes.footerBranding}>
            <Typography variant="body2" color="textSecondary">
              © {new Date().getFullYear()} <span className={classes.brandAccent}>Badal</span> by TELUS. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
